import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpParams,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@env/environment';
import {
  SampleSelectionRepository,
  SelectionConfirmation
} from '@service/sample-selection/sample-selection.repository';
import {
  SampleSelection,
  SampleTechnology
} from '@model/sample-selection/sample-selection.model';
import { SampleSelectionQueryParams } from '@model/sample-selection/sample-selection-query-params.model';
import { PaginatedResponse } from '@model/common/pagination.model';
import { HttpErrorHandlerService } from '@core/error-handling/http-error-handler.service';

/**
 * Backend DTO for specimen/sample
 */
interface SampleSelectionBackendDto {
  specimenId: string;
  specimenCode: string;
  concentration: number;
  ratio260280: number;
  ratio260230: number;
  extractionMethod: string;
  physicalLocation: string;
  responsible: string;
  lgCode: string;
  technology: string;
  priority: string;
  isReprocess: boolean;
  extractionDate: string; // ISO date string from backend
  testCodes?: string[]; // HU72.1: test subdivision codes (BRCA1, BRCA2, etc.)
  testSuffixes?: string[]; // HU72.1: LG suffix segments (MLPA_BRCA1, MLPA_BRCA2, etc.)
}

/**
 * Backend paginated response structure
 */
interface SampleSelectionBackendResponse {
  content: SampleSelectionBackendDto[];
  pageable: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
}

/**
 * Selection confirmation request DTO
 */
interface SelectSamplesRequest {
  technology: string;
  lgCodes: string[];
  maxLimit: number;
  selectedBy: string;
}

/**
 * Selection confirmation response DTO
 */
interface SelectionConfirmationDto {
  selectionId: string;
  technology: string;
  selectedCount: number;
  maxLimit: number;
  status: string;
  createdAt: string;
}

/**
 * HTTP Repository Implementation for Sample Selection
 * HU32-922499: Selección de muestras para procesamiento
 *
 * Connects to backend endpoints:
 * - GET /laboratorio/samples/selection
 * - POST /laboratorio/samples/select
 * - GET /laboratorio/samples/selection-limits
 *
 * This adapter follows the Dependency Inversion Principle:
 * - Domain layer defines the interface (SampleSelectionRepository)
 * - Infrastructure layer provides the implementation (HttpSampleSelectionRepository)
 */
@Injectable({
  providedIn: 'root'
})
export class HttpSampleSelectionRepository extends SampleSelectionRepository {
  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(HttpErrorHandlerService);
  private readonly BASE_URL = `${environment.laboratory.baseUrl}${environment.laboratory.context}/samples`;

  override findAll(
    params?: SampleSelectionQueryParams
  ): Observable<PaginatedResponse<SampleSelection>> {
    const httpParams = this.buildHttpParams(params);

    return this.http
      .get<SampleSelectionBackendResponse>(`${this.BASE_URL}/selection`, {
        params: httpParams
      })
      .pipe(
        map((response) => this.mapToDomain(response)),
        catchError((error: HttpErrorResponse) => {
          console.error('Error fetching sample selections:', error);
          return of(this.getEmptyResponse());
        })
      );
  }

  override confirmSelection(request: {
    technology: string;
    lgCodes: string[];
    maxLimit: number;
    selectedBy: string;
  }): Observable<SelectionConfirmation> {
    const body: SelectSamplesRequest = {
      technology: request.technology,
      lgCodes: request.lgCodes,
      maxLimit: request.maxLimit,
      selectedBy: request.selectedBy
    };

    return this.http
      .post<SelectionConfirmationDto>(`${this.BASE_URL}/select`, body)
      .pipe(
        map((response) => ({
          selectionId: response.selectionId,
          technology: response.technology,
          selectedCount: response.selectedCount,
          maxLimit: response.maxLimit,
          status: response.status,
          createdAt: response.createdAt
        })),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('confirmSelection', error)
        )
      );
  }

  override getSelectionLimits(): Observable<number[]> {
    return this.http.get<number[]>(`${this.BASE_URL}/selection-limits`).pipe(
      map((limits) => limits || []),
      catchError(() => of([12, 24, 36, 48, 60, 72, 84, 96]))
    );
  }

  /**
   * Build HTTP params from query parameters
   */
  private buildHttpParams(params?: SampleSelectionQueryParams): HttpParams {
    const technology = params?.technology ?? SampleTechnology.NGS;
    let httpParams = new HttpParams().set('technology', technology);

    if (params?.priority) {
      httpParams = httpParams.set('priority', params.priority);
    }

    httpParams = httpParams
      .set('page', String(params?.page ?? 0))
      .set('size', String(params?.size ?? 12))
      .set('sortBy', params?.sortBy ?? 'extractionDate')
      .set('sortDirection', params?.sortDirection ?? 'ASC');

    // Only send reprocessOnly when explicitly set to true
    if (params?.reprocessOnly === true) {
      httpParams = httpParams.set('reprocessOnly', 'true');
    }

    if (params?.sampleCount) {
      httpParams = httpParams.set('sampleCount', String(params.sampleCount));
    }

    return httpParams;
  }

  /**
   * Map backend response to domain model
   */
  private mapToDomain(
    response: SampleSelectionBackendResponse
  ): PaginatedResponse<SampleSelection> {
    return {
      content: response.content.map((dto) => this.mapSampleDto(dto)),
      pageable: {
        page: response.pageable.page,
        size: response.pageable.size,
        totalElements: response.pageable.totalElements,
        totalPages: response.pageable.totalPages,
        first: response.pageable.page === 0,
        last: response.pageable.page >= response.pageable.totalPages - 1
      }
    };
  }

  /**
   * Map individual sample DTO to domain model
   */
  private mapSampleDto(dto: SampleSelectionBackendDto): SampleSelection {
    return {
      specimenId: dto.specimenId,
      specimenCode: dto.specimenCode,
      concentration: dto.concentration,
      ratio260280: dto.ratio260280,
      ratio260230: dto.ratio260230,
      extractionMethod: dto.extractionMethod,
      physicalLocation: dto.physicalLocation,
      responsible: dto.responsible,
      lgCode: dto.lgCode,
      technology: dto.technology,
      priority: dto.priority,
      isReprocess: dto.isReprocess,
      extractionDate: new Date(dto.extractionDate),
      testCodes: dto.testCodes ?? [],
      testSuffixes: dto.testSuffixes ?? []
    };
  }

  /**
   * Get empty response for error handling
   */
  private getEmptyResponse(): PaginatedResponse<SampleSelection> {
    return {
      content: [],
      pageable: {
        page: 0,
        size: 12,
        totalElements: 0,
        totalPages: 0,
        first: true,
        last: true
      }
    };
  }
}

/**
 * @deprecated Use HttpSampleSelectionRepository instead
 */
export const SampleSelectionRepositoryAdapter = HttpSampleSelectionRepository;
